import { ArrowRight, Map, HomeIcon, Building2, ShieldCheck, FileCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { Eyebrow, MaskLineView, Reveal, CheckItem, ArrowCta, VerdeSpark } from "../components/ui-bits";
import { useLang } from "../i18n/LangContext";

const COPY = {
  en: {
    heroEyebrow: "Products · Portugal",
    heroL1: "Pre-construction",
    heroL2: "finance.",
    heroCopy:
      "Short-term, senior-secured financing for experienced real estate developers — from land acquisition, to licensing and pre-sale bridge loans.",
    heroCta: "Get indicative terms",
    stats: [
      { t: "€1–5m", s: "Typical facility" },
      { t: "Senior secured", s: "First-ranking security" },
      { t: "6–24 months", s: "Bridge to bank financing" },
      { t: "24 hours", s: "Indicative terms" },
    ],
    stagesEyebrow: "What it finances",
    stagesH1: "From land, to licence,",
    stagesH2: "to pre-sales.",
    stagesCopy:
      "One facility that follows your project through the stages a bank won't touch — the months before construction and construction-stage lending begin.",
    stages: [
      { icon: Map, img: "/stage-acquire.jpg", t: "Land acquisition", d: "Secure the site while the opportunity is there — before a bank will consider it." },
      { icon: HomeIcon, img: "/stage-unlock.jpg", t: "Licensing", d: "Fund costs and carry while the project moves through planning and licensing." },
      { icon: Building2, img: "/stage-bridge.jpg", t: "Pre-sale bridge loans", d: "Bridge the gap to pre-sales and construction-stage bank financing." },
    ],
    livingEyebrow: "Residential Categories",
    livingH1: "Finance for every",
    livingH2: "way of living.",
    livingCopy:
      "We finance the full spectrum of residential living — from homes for sale to rented, shared and serviced formats.",
    living: [
      { img: "/living-residential.jpg", t: "Residential", d: "Homes and apartments for sale." },
      { img: "/living-btr.jpg", t: "Build to rent", d: "Purpose-built rental schemes." },
      { img: "/living-student.jpg", t: "Student housing", d: "Purpose-built student residences." },
      { img: "/living-senior.jpg", t: "Senior housing", d: "Later-living residences." },
      { img: "/living-aparthotel.jpg", t: "Aparthotel", d: "Branded apart-hotels and extended stays." },
      { img: "/living-coliving.jpg", t: "Co-living", d: "Shared living with common spaces." },
      { img: "/living-serviced.jpg", t: "Serviced apartments", d: "Hotel-style serviced apartment buildings." },
      { img: "/living-hotels.jpg", t: "Hotels", d: "Boutique and lifestyle hotels." },
    ],
    forEyebrow: "Who it's for",
    forH1: "Built around experienced",
    forH2: "developers.",
    criteria: [
      "Experienced residential developers",
      "Portuguese residential projects",
      "Senior-secured facilities of €1–5m",
      "Repayment through bank refinancing, sale or pre-sales",
      "AI-assisted underwriting with local expert review",
    ],
    uwKicker: "Underwriting",
    uwH: "Decisions in days, not months.",
    uwCopy:
      "Verde — our AI underwriter — builds your case while our credit team and local experts test it. You receive indicative terms within 24 hours.",
    uwLink: "Meet our expert network",
    ctaH: "Start with your project.",
    ctaCopy: "Describe it below. Verde asks what's missing and builds your case.",
    ctaBtn: "Get indicative terms",
    ctaNote: "No obligation. All financing subject to review and approval.",
    footerNote:
      "All lending is subject to underwriting, security and full due diligence. Nothing on this page is an offer of credit.",
  },
  pt: {
    heroEyebrow: "Produtos · Portugal",
    heroL1: "Financiamento à",
    heroL2: "pré-construção.",
    heroCopy:
      "Financiamento de curto prazo com garantia sénior para promotores imobiliários experientes — da aquisição de terreno ao licenciamento e às pontes para pré-vendas.",
    heroCta: "Obter condições indicativas",
    stats: [
      { t: "€1–5m", s: "Operação típica" },
      { t: "Garantia sénior", s: "Segurança de primeiro grau" },
      { t: "6–24 meses", s: "Ponte até ao financiamento bancário" },
      { t: "24 horas", s: "Condições indicativas" },
    ],
    stagesEyebrow: "O que financia",
    stagesH1: "Do terreno, à licença,",
    stagesH2: "às pré-vendas.",
    stagesCopy:
      "Uma operação que acompanha o seu projeto pelas fases que um banco não toca — os meses antes de a construção e o crédito à construção começarem.",
    stages: [
      { icon: Map, img: "/stage-acquire.jpg", t: "Aquisição de terreno", d: "Garanta o terreno enquanto a oportunidade existe — antes de um banco o considerar." },
      { icon: HomeIcon, img: "/stage-unlock.jpg", t: "Licenciamento", d: "Financie custos e encargos enquanto o projeto avança no planeamento e licenciamento." },
      { icon: Building2, img: "/stage-bridge.jpg", t: "Ponte para pré-vendas", d: "Colmate a distância até às pré-vendas e ao crédito bancário em fase de construção." },
    ],
    livingEyebrow: "Categorias Residenciais",
    livingH1: "Financiamento para cada",
    livingH2: "forma de viver.",
    livingCopy:
      "Financiamos todo o espetro da habitação residencial — de casas para venda a formatos arrendados, partilhados e com serviços.",
    living: [
      { img: "/living-residential.jpg", t: "Residencial", d: "Moradias e apartamentos para venda." },
      { img: "/living-btr.jpg", t: "Arrendamento institucional", d: "Empreendimentos construídos para arrendamento." },
      { img: "/living-student.jpg", t: "Habitação para estudantes", d: "Residências estudantis de propósito construído." },
      { img: "/living-senior.jpg", t: "Habitação para seniores", d: "Residências para a terceira idade." },
      { img: "/living-aparthotel.jpg", t: "Apartahotel", d: "Apart-hoteis de marca e estadias prolongadas." },
      { img: "/living-coliving.jpg", t: "Co-living", d: "Habitação partilhada com espaços comuns." },
      { img: "/living-serviced.jpg", t: "Apartamentos de serviços", d: "Edifícios de apartamentos com serviços ao estilo de hotel." },
      { img: "/living-hotels.jpg", t: "Hotéis", d: "Hotéis boutique e lifestyle." },
    ],
    forEyebrow: "Para quem é",
    forH1: "Desenhado para promotores",
    forH2: "experientes.",
    criteria: [
      "Promotores residenciais experientes",
      "Projetos residenciais em Portugal",
      "Operações com garantia sénior de €1–5m",
      "Reembolso por refinanciamento bancário, venda ou pré-vendas",
      "Subscrição assistida por IA com revisão de especialistas locais",
    ],
    uwKicker: "Subscrição",
    uwH: "Decisões em dias, não em meses.",
    uwCopy:
      "A Verde — a nossa subscritora de IA — constrói o seu processo enquanto a nossa equipa de crédito e especialistas locais o testam. Recebe condições indicativas em 24 horas.",
    uwLink: "Conheça a nossa rede de especialistas",
    ctaH: "Comece pelo seu projeto.",
    ctaCopy: "Descreva-o abaixo. A Verde pergunta o que falta e constrói o seu processo.",
    ctaBtn: "Obter condições indicativas",
    ctaNote: "Sem compromisso. Todo o financiamento está sujeito a análise e aprovação.",
    footerNote:
      "Todo o crédito está sujeito a subscrição, garantias e due diligence completa. Nada nesta página constitui uma oferta de crédito.",
  },
  nl: {
    heroEyebrow: "Producten · Portugal",
    heroL1: "Financiering voor de",
    heroL2: "voorbouwfase.",
    heroCopy:
      "Kortlopende financiering met senior zekerheid voor ervaren vastgoedontwikkelaars — van grondverwerving en vergunningen tot overbrugging naar voorverkoop.",
    heroCta: "Indicatieve voorwaarden aanvragen",
    stats: [
      { t: "€1–5 mln", s: "Typische transactie" },
      { t: "Senior zekerheid", s: "Eerste rangs recht" },
      { t: "6–24 maanden", s: "Overbrugging naar bankfinanciering" },
      { t: "24 uur", s: "Indicatieve voorwaarden" },
    ],
    stagesEyebrow: "Wat er gefinancierd wordt",
    stagesH1: "Van grond, via vergunning,",
    stagesH2: "naar voorverkoop.",
    stagesCopy:
      "Eén transactie die uw project meeneemt door de fasen die een bank niet raakt — de maanden vóórdat de bouw en het bouwkrediet beginnen.",
    stages: [
      { icon: Map, img: "/stage-acquire.jpg", t: "Grondverwerving", d: "Zeker de grond terwijl de kans er is — vóórdat een bank er naar kijkt." },
      { icon: HomeIcon, img: "/stage-unlock.jpg", t: "Vergunningen", d: "Financier kosten en heffingen terwijl het project door bestemmingsplan en vergunningen gaat." },
      { icon: Building2, img: "/stage-bridge.jpg", t: "Overbrugging naar voorverkoop", d: "Overbrug de afstand naar voorverkoop en bankkrediet in de bouwfase." },
    ],
    livingEyebrow: "Wooncategorieën",
    livingH1: "Financiering voor elke",
    livingH2: "manier van wonen.",
    livingCopy:
      "Wij financieren het hele spectrum van residentiële woningbouw — van woningen voor verkoop tot verhuur-, gedeelde en voorzieningeformules.",
    living: [
      { img: "/living-residential.jpg", t: "Residentiëel", d: "Woningen en appartementen voor verkoop." },
      { img: "/living-btr.jpg", t: "Institutionele verhuur", d: "Projecten gebouwd voor verhuur (build to rent)." },
      { img: "/living-student.jpg", t: "Studentenhuisvesting", d: "Doelgebouwde studentencomplexen." },
      { img: "/living-senior.jpg", t: "Seniorenwoningen", d: "Woonvormen voor ouderen." },
      { img: "/living-aparthotel.jpg", t: "Aparthotel", d: "Merken-aparthotels en langdurige stays." },
      { img: "/living-coliving.jpg", t: "Co-living", d: "Gedeeld wonen met gemeenschappelijke ruimtes." },
      { img: "/living-serviced.jpg", t: "Serviced apartments", d: "Appartementengebouwen met hotelservice." },
      { img: "/living-hotels.jpg", t: "Hotels", d: "Boetiek- en lifestylehotels." },
    ],
    forEyebrow: "Voor wie is het",
    forH1: "Ontworpen voor ervaren",
    forH2: "ontwikkelaars.",
    criteria: [
      "Ervaren woningbouwontwikkelaars",
      "Residentiële projecten in Portugal",
      "Senior gedekte transacties van €1–5 mln",
      "Afbetaling via bankherfinanciering, verkoop of voorverkoop",
      "AI-ondersteunde beoordeling met controle door lokale experts",
    ],
    uwKicker: "Kredietbeoordeling",
    uwH: "Beslissingen in dagen, niet in maanden.",
    uwCopy:
      "Verde — onze AI-underwriter — bouwt uw dossier terwijl ons kredietteam en lokale experts het toetsen. U ontvangt indicatieve voorwaarden binnen 24 uur.",
    uwLink: "Ontmoet ons expertennetwerk",
    ctaH: "Begin bij uw project.",
    ctaCopy: "Beschrijf het hieronder. Verde vraagt wat ontbreekt en bouwt uw dossier.",
    ctaBtn: "Indicatieve voorwaarden aanvragen",
    ctaNote: "Geen verplichtingen. Alle financiering is onder voorbehoud van beoordeling en goedkeuring.",
    footerNote:
      "Alle kredietverlening is onder voorbehoud van kredietbeoordeling, zekerheden en volledige due diligence. Niets op deze pagina is een kredietaanbod.",
  },
};

export default function PreConstructionFinance() {
  const { lang, lp } = useLang();
  const c = COPY[lang];
  return (
    <div className="bg-cream">
      {/* ── HERO ─────────────────────────────────────────── */}
      <section className="relative overflow-hidden" data-testid="pcf-hero">
        <img
          src="/pcf-land-hero.jpg"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-verde-950/55" aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-r from-verde-950/90 via-verde-950/60 to-verde-950/20" aria-hidden="true" />
        <div className="container-x relative max-w-4xl pt-32 sm:pt-44 pb-20 lg:pb-28">
          <Eyebrow light>{c.heroEyebrow}</Eyebrow>
          <h1 className="display mt-6 text-[2.6rem] sm:text-5xl lg:text-[56px] text-cream" data-testid="pcf-headline">
            <MaskLineView>{c.heroL1}</MaskLineView>
            <MaskLineView delay={0.12}>{c.heroL2}</MaskLineView>
          </h1>
          <Reveal delay={0.25} className="mt-7 max-w-2xl">
            <p className="text-lg leading-relaxed text-cream/85">{c.heroCopy}</p>
          </Reveal>
          <Reveal delay={0.35} className="mt-9">
            <ArrowCta to={lp("/apply")} variant="light" testid="pcf-hero-cta">{c.heroCta}</ArrowCta>
          </Reveal>
        </div>
      </section>

      {/* ── STAT BAND ────────────────────────────────────── */}
      <section className="bg-white border-y border-sand" data-testid="pcf-stats">
        <div className="container-x grid grid-cols-2 lg:grid-cols-4 lg:divide-x lg:divide-sand">
          {c.stats.map((s) => (
            <div key={s.s} className="px-2 py-6 lg:py-8 lg:px-10">
              <p className="font-display text-xl lg:text-2xl font-black text-charcoal">{s.t}</p>
              <p className="mt-1 text-xs lg:text-sm text-fog">{s.s}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── WHAT IT FINANCES ─────────────────────────────── */}
      <section className="py-20 lg:py-28">
        <div className="container-x">
          <div className="max-w-2xl">
            <Eyebrow>{c.stagesEyebrow}</Eyebrow>
            <h2 className="display mt-5 text-3xl sm:text-4xl text-charcoal" data-testid="pcf-stages-headline">
              <MaskLineView>{c.stagesH1}</MaskLineView>
              <MaskLineView delay={0.1}>{c.stagesH2}</MaskLineView>
            </h2>
            <Reveal delay={0.2} className="mt-5">
              <p className="text-[15px] leading-relaxed text-fog">{c.stagesCopy}</p>
            </Reveal>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-3" data-testid="pcf-stages">
            {c.stages.map((s, i) => (
              <Reveal key={s.t} delay={0.08 + i * 0.08}>
                <div className="card h-full overflow-hidden" data-testid={`pcf-stage-${i}`}>
                  <div className="relative p-3 pb-0">
                    <img loading="lazy" decoding="async" src={s.img} alt={s.t} className="h-28 w-full rounded-xl object-cover" />
                    <span className="relative flex h-11 w-11 items-center justify-center overflow-hidden rounded-2xl bg-gradient-to-br from-[#EAF3EE] via-[#E0EEE6] to-[#CFE5D9] ring-1 ring-verde-800/10 shadow-[0_12px_26px_-16px_rgba(7,39,29,0.45)]">
                      <span className="absolute inset-0 bg-gradient-to-b from-white/70 via-transparent to-transparent" aria-hidden="true" />
                      <s.icon className="relative h-5 w-5 text-verde-800" strokeWidth={1.7} aria-hidden="true" />
                    </span>
                  </div>
                  <div className="p-6 pt-8">
                    <h3 className="font-display text-lg font-bold text-charcoal">{s.t}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-ink">{s.d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── LIVING CATEGORIES ────────────────────────────── */}
      <section className="py-20 lg:py-28 border-t border-sand">
        <div className="container-x">
          <div className="max-w-2xl">
            <Eyebrow>{c.livingEyebrow}</Eyebrow>
            <h2 className="display mt-5 text-3xl sm:text-4xl text-charcoal" data-testid="pcf-living-headline">
              <MaskLineView>{c.livingH1}</MaskLineView>
              <MaskLineView delay={0.1}>{c.livingH2}</MaskLineView>
            </h2>
            <Reveal delay={0.2} className="mt-5">
              <p className="text-[15px] leading-relaxed text-fog">{c.livingCopy}</p>
            </Reveal>
          </div>

          <div className="mt-12 grid grid-cols-2 gap-5 lg:grid-cols-4" data-testid="pcf-living">
            {c.living.map((cat, i) => (
              <Reveal key={cat.t} delay={0.05 + (i % 4) * 0.07}>
                <div className="card h-full overflow-hidden" data-testid={`pcf-living-${i}`}>
                  <img loading="lazy" decoding="async" src={cat.img} alt={cat.t} className="h-28 w-full object-cover sm:h-32" />
                  <div className="p-5">
                    <h3 className="font-display text-base font-bold text-charcoal">{cat.t}</h3>
                    <p className="mt-1.5 text-[13px] leading-relaxed text-ink">{cat.d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHO IT'S FOR + UNDERWRITING ──────────────────── */}
      <section className="py-20 lg:py-28 bg-white border-y border-sand">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-14 items-center">
          <div className="lg:col-span-7">
            <Eyebrow>{c.forEyebrow}</Eyebrow>
            <h2 className="display mt-5 text-3xl sm:text-4xl text-charcoal" data-testid="pcf-criteria-headline">
              <MaskLineView>{c.forH1}</MaskLineView>
              <MaskLineView delay={0.1}>{c.forH2}</MaskLineView>
            </h2>
            <Reveal delay={0.2}>
              <ul className="mt-8 space-y-4" data-testid="pcf-criteria">
                {c.criteria.map((line) => (
                  <CheckItem key={line}>{line}</CheckItem>
                ))}
              </ul>
            </Reveal>
          </div>

          <div className="lg:col-span-5" data-testid="pcf-underwriting-panel">
            <Reveal delay={0.15}>
              <div className="rounded-2xl bg-verde-950 p-8 lg:p-10 text-cream">
                <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-mint/80">
                  <VerdeSpark className="h-4 w-4" /> {c.uwKicker}
                </p>
                <p className="mt-5 font-display text-2xl font-bold leading-snug">{c.uwH}</p>
                <p className="mt-4 text-sm leading-relaxed text-cream/80">{c.uwCopy}</p>
                <Link
                  to={lp("/expert-network")}
                  data-testid="pcf-experts-link"
                  className="group mt-7 inline-flex items-center gap-2 text-sm font-semibold text-mint transition-colors hover:text-cream"
                >
                  {c.uwLink}
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── CTA BAND ─────────────────────────────────────── */}
      <section className="py-20 lg:py-28">
        <div className="container-x">
          <Reveal>
            <div className="rounded-2xl bg-verde-950 px-8 py-14 text-center lg:py-20" data-testid="pcf-cta-band">
              <h2 className="display mx-auto max-w-2xl text-3xl sm:text-4xl lg:text-5xl text-cream">
                <MaskLineView>{c.ctaH}</MaskLineView>
              </h2>
              <p className="mx-auto mt-5 max-w-xl text-sm lg:text-base text-cream/75">{c.ctaCopy}</p>
              <div className="mt-9 flex justify-center">
                <ArrowCta to={lp("/apply")} variant="light" testid="pcf-cta">{c.ctaBtn}</ArrowCta>
              </div>
              <p className="mt-6 text-xs text-cream/50">{c.ctaNote}</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="pb-16">
        <div className="container-x max-w-3xl">
          <p className="border-t border-sand pt-6 text-xs leading-relaxed text-fog" data-testid="pcf-footer-note">
            {c.footerNote}
          </p>
        </div>
      </section>
    </div>
  );
}
